import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import BottomNav from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { openRazorpayCheckout } from '@/lib/razorpay';

export default function ProviderPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [intro, setIntro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get('/providers/me/plans')
      .then(({ data }) => {
        setPlans(data.plans || []);
        setIntro(data.introductory);
      })
      .catch(() => toast.error('Could not load plans'))
      .finally(() => setLoading(false));
  }, []);

  const recharge = async (planKey) => {
    setPaying(true);
    try {
      const { data } = await api.post('/providers/me/recharge/order', { plan_key: planKey });
      const checkout = {
        ...data.checkout,
        key_id: data.checkout.key_id,
      };
      await openRazorpayCheckout({
        checkout,
        description: `Recharge — ${planKey}`,
        onSuccess: async (response) => {
          try {
            await api.post('/providers/me/recharge/verify', {
              payment_id: data.payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Recharge successful');
            navigate('/provider/dashboard');
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Verification failed');
          } finally {
            setPaying(false);
          }
        },
        onDismiss: () => setPaying(false),
        onFailed: () => {
          toast.error('Payment failed');
          setPaying(false);
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recharge failed');
      setPaying(false);
    }
  };

  return (
    <div className="resqly-shell">
      <div className="resqly-frame flex flex-col min-h-screen">
        <MobileShell title="Recharge Plans" header>
          <div className="px-5 py-5 space-y-4">
            {intro?.orders_remaining > 0 && (
              <div className="resqly-card p-4 text-sm" data-testid="intro-status">
                Introductory rate: {intro.commission_percent}% — {intro.orders_remaining} orders remaining
              </div>
            )}
            {loading ? <p className="text-slate-500">Loading...</p> : plans.map((plan) => (
              <div key={plan.plan_key} className="resqly-card p-4" data-testid={`plan-${plan.plan_key}`}>
                <div className="font-semibold text-lg">{plan.name}</div>
                <div className="text-sm text-slate-600 mt-1">{plan.description}</div>
                <div className="mt-2 text-sm">Price: ₹{plan.price}</div>
                <div className="text-sm">Commission: {plan.commission_percent}%</div>
                <Button
                  className="mt-3 w-full"
                  disabled={paying}
                  data-testid={`recharge-${plan.plan_key}`}
                  onClick={() => recharge(plan.plan_key)}
                >
                  {paying ? 'Processing…' : `Recharge ₹${plan.price}`}
                </Button>
              </div>
            ))}
          </div>
        </MobileShell>
        <BottomNav variant="provider" />
      </div>
    </div>
  );
}
